/**
 * Lab-compliant toast/alert/confirm API.
 *
 * Backend for the `Lab.toast()`, `Lab.alert()`, `Lab.confirm()` calls that
 * are mandatory in this project (native `window.alert/confirm/prompt` are
 * strictly forbidden by CLAUDE.md).
 *
 * Usage :
 *   import { Lab } from '@/lib/toast';
 *   Lab.toast('Paramètres enregistrés.', 'pass');
 *   const ok = await Lab.confirm('Supprimer ?', 'Confirmation');
 *
 * Mount `<ToastHost />` once near the root (done in App.tsx).
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, Modal } from '@/components/ui';

type ToastKind = 'info' | 'pass' | 'success' | 'fail' | 'error' | 'warn';

interface Toast {
  id: number;
  msg: string;
  kind: ToastKind;
}

interface ConfirmRequest {
  id: number;
  msg: string;
  title: string;
  resolve: (ok: boolean) => void;
  danger: boolean;
}

interface AlertRequest {
  id: number;
  msg: string;
  title: string;
  resolve: () => void;
}

/* ── Event bus (module-level so any file can call Lab.* without Context) ── */

type ToastFn = (msg: string, kind?: ToastKind) => void;
type AlertFn = (msg: string, title?: string) => Promise<void>;
type ConfirmFn = (msg: string, title?: string, opts?: { danger?: boolean }) => Promise<boolean>;

const handlers: {
  toast?:   ToastFn;
  alert?:   AlertFn;
  confirm?: ConfirmFn;
} = {};

export const Lab = {
  toast: (msg: string, kind: ToastKind = 'info') => {
    if (handlers.toast) handlers.toast(msg, kind);
    else if (typeof console !== 'undefined') console.log(`[Lab.toast:${kind}]`, msg);
  },
  alert: (msg: string, title?: string) => {
    if (handlers.alert) return handlers.alert(msg, title);
    return Promise.resolve();
  },
  confirm: (msg: string, title?: string, opts?: { danger?: boolean }) => {
    if (handlers.confirm) return handlers.confirm(msg, title, opts);
    return Promise.resolve(false);
  },
};

const TOAST_ICON: Record<ToastKind, string> = {
  info: 'ℹ', pass: '✓', success: '✓', fail: '✗', error: '✗', warn: '⚠',
};
const TOAST_COLOR: Record<ToastKind, string> = {
  info:    'var(--info)',
  pass:    'var(--success)',
  success: 'var(--success)',
  fail:    'var(--danger)',
  error:   'var(--danger)',
  warn:    'var(--warning)',
};

/* ── React host ──────────────────────────────────────────────────────── */

const Ctx = createContext<null>(null);

export function ToastHost({ children }: { children?: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirms, setConfirms] = useState<ConfirmRequest[]>([]);
  const [alerts, setAlerts]     = useState<AlertRequest[]>([]);
  const idRef = useRef(0);

  const addToast = useCallback<ToastFn>((msg, kind = 'info') => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, msg, kind }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4500);
  }, []);

  const addAlert = useCallback<AlertFn>((msg, title = 'Information') => {
    const id = ++idRef.current;
    return new Promise<void>((resolve) => {
      setAlerts((prev) => [...prev, { id, msg, title, resolve }]);
    });
  }, []);

  const addConfirm = useCallback<ConfirmFn>((msg, title = 'Confirmation', opts) => {
    const id = ++idRef.current;
    const danger = opts?.danger !== false;
    return new Promise<boolean>((resolve) => {
      setConfirms((prev) => [...prev, { id, msg, title, resolve, danger }]);
    });
  }, []);

  useEffect(() => {
    handlers.toast   = addToast;
    handlers.alert   = addAlert;
    handlers.confirm = addConfirm;
    return () => {
      handlers.toast = undefined;
      handlers.alert = undefined;
      handlers.confirm = undefined;
    };
  }, [addToast, addAlert, addConfirm]);

  const closeAlert = (id: number) => setAlerts((prev) => {
    const next = prev.filter((a) => a.id !== id);
    const removed = prev.find((a) => a.id === id);
    removed?.resolve();
    return next;
  });

  const answerConfirm = (id: number, ok: boolean) => setConfirms((prev) => {
    const next = prev.filter((c) => c.id !== id);
    const removed = prev.find((c) => c.id === id);
    removed?.resolve(ok);
    return next;
  });

  return (
    <Ctx.Provider value={null}>
      {children}

      {/* Toasts bottom-right */}
      {typeof document !== 'undefined' && createPortal(
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 24, right: 24,
            display: 'flex', flexDirection: 'column', gap: 8,
            pointerEvents: 'none',
            zIndex: 1600,
            maxWidth: 380,
          }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              style={{
                pointerEvents: 'auto',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 14px',
                background: 'var(--surface-1)',
                border: `1px solid ${TOAST_COLOR[t.kind]}`,
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--elev-3)',
                fontSize: 13,
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)',
                cursor: 'pointer',
              }}
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            >
              <span style={{ color: TOAST_COLOR[t.kind], flexShrink: 0, fontSize: 14 }}>
                {TOAST_ICON[t.kind]}
              </span>
              <span style={{ wordBreak: 'break-word' }}>{t.msg}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}

      {/* Alerts */}
      {alerts.map((a) => (
        <ModalDialog
          key={a.id}
          title={a.title}
          message={a.msg}
          onClose={() => closeAlert(a.id)}
          buttons={[
            { label: 'OK', primary: true, onClick: () => closeAlert(a.id) },
          ]}
        />
      ))}

      {/* Confirms */}
      {confirms.map((c) => (
        <ModalDialog
          key={c.id}
          title={c.title}
          message={c.msg}
          onClose={() => answerConfirm(c.id, false)}
          buttons={[
            { label: 'Annuler', onClick: () => answerConfirm(c.id, false) },
            { label: 'Confirmer', primary: true, danger: c.danger, onClick: () => answerConfirm(c.id, true) },
          ]}
        />
      ))}
    </Ctx.Provider>
  );
}

function ModalDialog({
  title, message, onClose, buttons,
}: {
  title: string;
  message: string;
  onClose: () => void;
  buttons: { label: string; primary?: boolean; danger?: boolean; onClick: () => void }[];
}) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <Modal open onClose={onClose} title={title} size="sm">
      <div style={{
        padding: 20,
        fontSize: 13,
        color: 'var(--text-secondary)',
        lineHeight: 1.55,
        whiteSpace: 'pre-line',
        flex: '1 1 auto',
        overflowY: 'auto',
        minHeight: 0,
      }}>
        {message}
      </div>
      <footer style={{
        padding: '10px 18px',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        flexShrink: 0,
      }}>
        {buttons.map((b, i) => (
          <Button
            key={i}
            variant={b.primary ? (b.danger ? 'danger' : 'primary') : 'ghost'}
            size="sm"
            onClick={b.onClick}
          >
            {b.label}
          </Button>
        ))}
      </footer>
    </Modal>,
    document.body,
  );
}

/** Optional hook for components that prefer a hook style. */
export function useToast() {
  useContext(Ctx);
  return Lab;
}
